import { ssoApi } from "@sso/apis";
import { ApiMethodKeys, EntityName, SnackbarKeys, notificationsMsgs } from "@sso/constant";
import { closeDialog } from "@sso/store/slices/dialog";
import { pushSnackbar } from "@sso/store/slices/snackbar";
import {
  ApiPageResponse,
  ID,
  SoftwareLicence,
  SoftwareLicenceCreateRequest,
  SoftwareLicenceSummary,
  SoftwareLicenceUpdateRequest,
} from "@sso/types";

import { CacheKeys } from "../cacheKeys";
import { Endpoints } from "../endpoints";

export const softwareLicenceApi = ssoApi
  .enhanceEndpoints({ addTagTypes: [CacheKeys.SOFTWARE_LICENCES] })
  .injectEndpoints({
    endpoints: builder => ({
      softwareLicences: builder.query<ApiPageResponse<SoftwareLicenceSummary>, string>({
        query: query => ({ url: `${Endpoints.SOFTWARE_LICENCES}?${query}` }),
        providesTags: [CacheKeys.SOFTWARE_LICENCES],
      }),
      getSoftwareLicenceById: builder.query<SoftwareLicence, ID>({
        query: softwareLicenceId => ({
          url: `${Endpoints.SOFTWARE_LICENCES}/${softwareLicenceId}`,
        }),
        transformResponse: (response: any) => response.result,
        providesTags: [CacheKeys.SOFTWARE_LICENCES],
      }),
      addSoftwareLicence: builder.mutation<SoftwareLicence, SoftwareLicenceCreateRequest>({
        query: data => ({ url: `${Endpoints.SOFTWARE_LICENCES}`, method: ApiMethodKeys.POST, data }),
        invalidatesTags: [CacheKeys.SOFTWARE_LICENCES],
        onQueryStarted(_, { dispatch, queryFulfilled }) {
          queryFulfilled.then(() => {
            dispatch(
              pushSnackbar({
                type: SnackbarKeys.SUCCESS,
                title: notificationsMsgs.ADD_ITEM(EntityName.SOFTWARE_LICENCE),
              }),
            );
            dispatch(closeDialog());
          });
        },
      }),
      updateSoftwareLicence: builder.mutation<SoftwareLicence, SoftwareLicenceUpdateRequest>({
        query: data => ({
          url: `${Endpoints.SOFTWARE_LICENCES}`,
          method: ApiMethodKeys.PUT,
          data,
        }),
        transformResponse: (response: any) => response.result,
        invalidatesTags: [CacheKeys.SOFTWARE_LICENCES],
        onQueryStarted(_, { dispatch, queryFulfilled }) {
          queryFulfilled.then(() => {
            dispatch(
              pushSnackbar({
                type: SnackbarKeys.SUCCESS,
                title: notificationsMsgs.Edit_ITEM(EntityName.SOFTWARE_LICENCE),
              }),
            );
            dispatch(closeDialog());
          });
        },
      }),
      deleteSoftwareLicence: builder.mutation<void, string>({
        query: softwareLicenceId => ({
          url: Endpoints.SOFTWARE_LICENCES,
          params: { softwareLicenceId },
          method: ApiMethodKeys.DELETE,
        }),
        invalidatesTags: [CacheKeys.SOFTWARE_LICENCES],
        onQueryStarted(_, { dispatch, queryFulfilled }) {
          queryFulfilled.then(() => {
            dispatch(
              pushSnackbar({
                type: SnackbarKeys.SUCCESS,
                title: notificationsMsgs.DELETE_ITEM(EntityName.SOFTWARE_LICENCE),
              }),
            );
            dispatch(closeDialog());
          });
        },
      }),
    }),
  });

export const {
  useSoftwareLicencesQuery,
  useGetSoftwareLicenceByIdQuery,
  useAddSoftwareLicenceMutation,
  useDeleteSoftwareLicenceMutation,
  useUpdateSoftwareLicenceMutation,
} = softwareLicenceApi;
